'use client';

/**
 * Nodejs.tsx — produktside for inNode (`/nodejs`)
 *
 * Følger ibm.com/products-mønsteret, som /webhotell, /vps, /wordpress og /infiles:
 * brødsmulesti og hero i grått bånd, fast seksjonsmeny, oversikt, funksjoner
 * (akkordion), promo, pakker (tre priskort), sammenligning, nummererte steg
 * («Kom i gang»), bruksområder, relaterte tjenester, spørsmål og avsluttende CTA.
 * Komponentene ligger i src/components/product/ og deles med de andre produktsidene.
 * Ingen UI Shell (ROUTES_WITHOUT_SHELL i ClientUiShell.tsx).
 *
 * Innhold: statisk, norsk + engelsk, se node-content.ts (plassholdere + varemerkenotat).
 */

import ClosingCta from '@/components/landing/ClosingCta';
import LandingFooter from '@/components/landing/LandingFooter';
import LandingHeader from '@/components/landing/LandingHeader';
import { MASTHEAD_CONTENT } from '@/components/landing/masthead-content';
import NodeHeroIllustration from '@/components/innode/NodeHeroIllustration';
import NodePanelIllustration from '@/components/innode/NodePanelIllustration';
import ComparisonTable from '@/components/product/ComparisonTable';
import FaqAccordion from '@/components/product/FaqAccordion';
import FeatureAccordion from '@/components/product/FeatureAccordion';
import NumberedSteps from '@/components/product/NumberedSteps';
import PricingCards from '@/components/product/PricingCards';
import ProductHero from '@/components/product/ProductHero';
import PromoBanner from '@/components/product/PromoBanner';
import RelatedProducts from '@/components/product/RelatedProducts';
import SectionHeader from '@/components/product/SectionHeader';
import SectionNav from '@/components/product/SectionNav';
import UseCases from '@/components/product/UseCases';
import styles from '@/components/product/product.module.css';
import { useLocale } from '@/hooks/useLocale';
import landingStyles from '@/sections/landing/landing.module.css';
import { NODE_CONTENT } from './node-content';

export default function Nodejs() {
  const { locale, setLocale } = useLocale();
  const content = NODE_CONTENT[locale];

  return (
    <div className={landingStyles.page}>
      <LandingHeader ui={content.ui} menu={MASTHEAD_CONTENT[locale]} locale={locale} onLocaleChange={setLocale} />
      <main id="innhold" className={landingStyles.main}>
        <ProductHero
          hero={content.hero}
          breadcrumb={content.breadcrumb}
          illustration={<NodeHeroIllustration label={content.hero.illustrationLabel} />}
        />

        <SectionNav nav={content.sectionNav} />

        <section id="oversikt" className={styles.section} aria-labelledby="overview-heading">
          <div className={styles.container}>
            <SectionHeader
              id="overview-heading"
              eyebrow={content.overview.eyebrow}
              heading={content.overview.heading}
              blue
            >
              {content.overview.body.map((paragraph) => (
                <p key={paragraph} className={styles.lead}>
                  {paragraph}
                </p>
              ))}
            </SectionHeader>
          </div>
        </section>

        <FeatureAccordion
          features={content.features}
          art={<NodePanelIllustration label={content.features.panelLabel} />}
        />
        <PromoBanner promo={content.promo} />
        <PricingCards plans={content.plans} />
        <ComparisonTable comparison={content.comparison} plans={content.plans} />
        <NumberedSteps
          id="kom-i-gang"
          steps={content.steps}
          art={<NodePanelIllustration label={content.steps.panelLabel} />}
        />
        <UseCases useCases={content.useCases} />
        <RelatedProducts related={content.related} />
        <FaqAccordion faq={content.faq} />
        <ClosingCta cta={content.cta} />
      </main>
      <LandingFooter
        tagline={content.footer.tagline}
        legal={content.footer.legal}
        columns={[
          {
            heading: content.footer.platformLabel,
            links: [
              { label: 'inSuite', href: '/' },
              { label: content.ui.login, href: '/login' },
            ],
          },
          {
            heading: content.footer.sectionsLabel,
            links: content.sectionNav.items.map((item) => ({ label: item.label, href: `#${item.id}` })),
          },
        ]}
      />
    </div>
  );
}
